import React from "react";
import { Row, Col, Card, Button, Badge, Space, Tag } from "antd";
import { PrinterOutlined, UserOutlined, PhoneOutlined, EnvironmentOutlined, CreditCardOutlined, FileTextOutlined } from "@ant-design/icons";
import Swiggy from "../../assets/images/swiggy1.svg";
import Zomato from "../../assets/images/zomato.svg";
import SwiggyBolt from "../../assets/images/swiggyBolt.svg";
import WhatServe from "../../assets/images/whatServe.jpeg";

const OrderDetails = ({ selectedOrder, statusDisplayMapping, onPrint, actionButtons }) => {
  const channelIcons = { swiggy: Swiggy, zomato: Zomato, swiggybolt: SwiggyBolt, whatsserve: WhatServe };

  const formatCurrency = (amount) => `₹${amount?.toFixed(2) || "0"}`;
  const formatTime = (timeString) => new Date(timeString).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });

  const statusConfig = {
    NEW: { color: "#4F23F1", label: statusDisplayMapping["NEW"] || "New" },
    AK: { color: "#E99013", label: statusDisplayMapping["AK"] || "Acknowledged" },
    FR: { color: "#008000", label: statusDisplayMapping["FR"] || "Food Ready" },
    DI: { color: "#4F23F1", label: statusDisplayMapping["DI"] || "Dispatched" },
    CC: { color: "#dc2626", label: statusDisplayMapping["CC"] || "Cancelled" },
    CO: { color: "#07f307", label: statusDisplayMapping["CO"] || "Completed" },
    default: { color: "#4F23F1", label: statusDisplayMapping["default"] || "Unknown" },
  };

  if (!selectedOrder) {
    return (
      <Card
        style={{
          borderRadius: "8px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          height: "calc(100vh - 120px)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            color: "#6b7280",
            fontSize: "12px",
          }}
        >
          Click on any order to view details
        </div>
      </Card>
    );
  }

  const isInstantOrder = selectedOrder?.metaData?.find((md) => md.key === "Instant Order")?.value === "true";
  const channelIcon = channelIcons[selectedOrder.channel.toLowerCase()] || (isInstantOrder ? SwiggyBolt : selectedOrder.channel.toLowerCase() === "swiggy" ? Swiggy : Zomato);

  return (
    <Card
      style={{
        borderRadius: "8px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        height: "100%",
      }}
      title={
        <Row justify="space-between" align="middle">
          <Col>
            <span style={{ fontSize: "14px", fontWeight: 600 }}>Order Details</span>
          </Col>
          <Col>
            <Button icon={<PrinterOutlined />} onClick={onPrint} style={{ height: "28px", width: "28px", borderRadius: "6px" }} />
          </Col>
        </Row>
      }
    >
      <div style={{ maxHeight: "calc(100vh - 160px)", overflowY: "auto", padding: "0 12px 12px" }}>
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          {/* Order Header */}
          <Row justify="space-between" align="middle">
            <Col>
              <Row align="middle" gutter={8}>
                <Col>
                  <div style={{ fontSize: "16px", fontWeight: 600 }}>{selectedOrder.orderNo}</div>
                  <div style={{ fontSize: "10px", color: "#6b7280" }}>{formatTime(selectedOrder.orderTime)}</div>
                </Col>
              </Row>
            </Col>
            <Col>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: "14px", fontWeight: 600 }}>{formatCurrency(selectedOrder.amount)}</div>
                <Tag
                  style={{
                    backgroundColor: `${statusConfig[selectedOrder.status]?.color || statusConfig.default.color}20`,
                    color: statusConfig[selectedOrder.status]?.color || statusConfig.default.color,
                    border: `1px solid ${statusConfig[selectedOrder.status]?.color || statusConfig.default.color}40`,
                    fontSize: "10px",
                    padding: "2px 6px",
                    fontWeight: "bold",
                    borderRadius: "5px",
                  }}
                >
                  {statusConfig[selectedOrder.status]?.label || statusConfig.default.label}
                </Tag>
              </div>
            </Col>
          </Row>

          {/* Customer Information */}
          <div>
            <div style={{ fontSize: "12px", fontWeight: 600, marginBottom: "4px" }}>Customer Information</div>
            <Space direction="vertical" size={4} style={{ fontSize: "12px" }}>
              <Row align="middle" gutter={8}>
                <Col>
                  <UserOutlined style={{ fontSize: "12px" }} />
                </Col>
                <Col>{selectedOrder.customerName || "N/A"}</Col>
              </Row>
              <Row align="middle" gutter={8}>
                <Col>
                  <PhoneOutlined style={{ fontSize: "12px" }} />
                </Col>
                <Col style={{ color: "#6b7280", fontSize: "10px" }}>{selectedOrder.customerPhone || "N/A"}</Col>
              </Row>
              <Row align="middle" gutter={8}>
                <Col>
                  <EnvironmentOutlined style={{ fontSize: "12px" }} />
                </Col>
                <Col style={{ color: "#6b7280", fontSize: "10px" }}>{selectedOrder.customerAddress || "N/A"}</Col>
              </Row>
            </Space>
          </div>

          {/* Order Items */}
          <div>
            <div style={{ fontSize: "12px", fontWeight: 600, marginBottom: "4px" }}>Order Items</div>
            <Space direction="vertical" size={4} style={{ width: "100%" }}>
              {selectedOrder.items.map((item, index) => (
                <Row key={index} justify="space-between" align="middle" style={{ fontSize: "12px" }}>
                  <Col>
                    <span style={{ fontWeight: 500 }}>{item.name}</span>
                    <span style={{ color: "#6b7280", marginLeft: "8px" }}>x{item.quantity}</span>
                    {item.addons?.length > 0 && (
                      <div style={{ marginLeft: "16px", color: "#6b7280", fontSize: "10px" }}>
                        {item.addons.map((addon, idx) => (
                          <div key={idx}>
                            - {addon.name} (Qty: {addon.qty}, {formatCurrency(addon.price)})
                          </div>
                        ))}
                      </div>
                    )}
                  </Col>
                  <Col>{formatCurrency(item.price * item.quantity)}</Col>
                </Row>
              ))}
            </Space>
          </div>

          {/* Order Summary */}
          <div style={{ borderTop: "1px solid #d1d5db", paddingTop: "8px" }}>
            <Space direction="vertical" size={4} style={{ width: "100%" }}>
              <Row justify="space-between" style={{ fontSize: "12px" }}>
                <Col>Subtotal</Col>
                <Col>{formatCurrency(selectedOrder.amount - selectedOrder.tax)}</Col>
              </Row>
              <Row justify="space-between" style={{ fontSize: "12px" }}>
                <Col>Tax</Col>
                <Col>{formatCurrency(selectedOrder.tax)}</Col>
              </Row>
              {selectedOrder.discount > 0 && (
                <Row justify="space-between" style={{ fontSize: "12px", color: "#16a34a" }}>
                  <Col>Discount</Col>
                  <Col>-{formatCurrency(selectedOrder.discount)}</Col>
                </Row>
              )}
              <Row justify="space-between" style={{ fontSize: "12px", fontWeight: 600, borderTop: "1px solid #d1d5db", paddingTop: "4px" }}>
                <Col>Total</Col>
                <Col>{formatCurrency(selectedOrder.amount)}</Col>
              </Row>
            </Space>
          </div>

          {/* Additional Info */}
          <div>
            <Space direction="vertical" size={4} style={{ width: "100%" }}>
              <Row align="middle" gutter={8} style={{ fontSize: "12px" }}>
                <Col>
                  <CreditCardOutlined style={{ fontSize: "12px" }} />
                </Col>
                <Col>{selectedOrder.paymentMode || "N/A"}</Col>
              </Row>
              <Row align="middle" gutter={8} style={{ fontSize: "12px" }}>
                <Col>
                  <img src={channelIcon} style={{ height: "20px" }} alt={selectedOrder.channel} />
                </Col>
                <Col>{selectedOrder.channel}</Col>
              </Row>
              {selectedOrder.metaData?.find((md) => md.key === "Instructions") && (
                <div>
                  <div style={{ fontSize: "12px", fontWeight: 600, marginBottom: "4px" }}>Order Instructions</div>
                  <Row align="middle" gutter={8} style={{ fontSize: "10px", color: "#6b7280" }}>
                    <Col>
                      <FileTextOutlined style={{ fontSize: "12px" }} />
                    </Col>
                    <Col>{selectedOrder.metaData.find((md) => md.key === "Instructions")?.value || "N/A"}</Col>
                  </Row>
                </div>
              )}
              {selectedOrder.metaData?.find((md) => md.key === "Charges") && (
                <div>
                  <div style={{ fontSize: "12px", fontWeight: 600, marginBottom: "4px" }}>Packaging Charges</div>
                  {(() => {
                    try {
                      const chargesData = selectedOrder.metaData.find((md) => md.key === "Charges");
                      const parsedCharges = JSON.parse(chargesData.value);
                      return parsedCharges.map((charge, index) => (
                        <Row key={index} justify="space-between" style={{ fontSize: "10px" }}>
                          <Col>{charge.title}</Col>
                          <Col>{formatCurrency(charge.value)}</Col>
                        </Row>
                      ));
                    } catch (e) {
                      console.error("Failed to parse charges metadata:", e);
                      return null;
                    }
                  })()}
                </div>
              )}
              {selectedOrder.metaData?.find((md) => md.key === "Rider Status") && (
                <div>
                  <div style={{ fontSize: "12px", fontWeight: 600, marginBottom: "4px" }}>Delivery Info</div>
                  {(() => {
                    try {
                      const riderData = selectedOrder.metaData.find((md) => md.key === "Rider Status");
                      const parsedRider = JSON.parse(riderData.value);
                      const riderDetails = parsedRider?.delivery_info?.delivery_person_details;
                      return (
                        <Space direction="vertical" size={4}>
                          {riderDetails?.name && riderDetails.name !== "Unknown" && (
                            <Row justify="space-between" style={{ fontSize: "10px" }}>
                              <Col>Rider Name</Col>
                              <Col>{riderDetails.name}</Col>
                            </Row>
                          )}
                          {riderDetails?.phone && riderDetails.phone !== "N/A" && (
                            <Row justify="space-between" style={{ fontSize: "10px" }}>
                              <Col>Rider Phone No</Col>
                              <Col>{riderDetails.phone}</Col>
                            </Row>
                          )}
                          {parsedRider?.delivery_info?.current_state && (
                            <Row justify="space-between" style={{ fontSize: "10px" }}>
                              <Col>Rider Status</Col>
                              <Col>
                                <Badge style={{ padding: "2px 6px" }} text={parsedRider.delivery_info.current_state} />
                              </Col>
                            </Row>
                          )}
                        </Space>
                      );
                    } catch (e) {
                      console.error("Failed to parse rider metadata:", e);
                      return null;
                    }
                  })()}
                </div>
              )}
            </Space>
          </div>

          {/* Action Buttons */}
          <div style={{ borderTop: "1px solid #d1d5db", paddingTop: "8px" }}>
            <div style={{ fontSize: "12px", fontWeight: 600, marginBottom: "8px" }}>Quick Actions</div>
            <Row gutter={[8, 8]}>{actionButtons}</Row>
          </div>
        </Space>
      </div>
      <style jsx>{`
        .ant-card {
          border-radius: 8px !important;
        }
        .ant-card-head {
          padding: 8px 12px !important;
          border-bottom: 1px solid #d1d5db !important;
        }
        .ant-card-body {
          padding: 0 !important;
        }
        .ant-tag {
          margin-right: 0 !important;
        }
      `}</style>
    </Card>
  );
};

export default OrderDetails;
